import { useReactiveVar } from '@apollo/client';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { NavigateNext } from '@mui/icons-material';
import { Breadcrumbs, Card, CardContent, Divider, Link } from '@mui/material';
import { useState } from 'react';
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import AddPaymentMethod from './AddPaymentMethod';

import AddressAutocomplete from '@/components/AddressAutocomplete';
import BillingWeekFormField from '@/components/BillingWeekFormField';
import Button from '@/components/Button';
import ConditionalWrapper from '@/components/ConditionalWrapper';
import FormElement from '@/components/FormElement';
import GroupInvoicesFormField from '@/components/GroupInvoicesFormField';
import InvoiceDurationFormField from '@/components/InvoiceDurationFormField';
import ItemSelect from '@/components/ItemSelect';
import Option from '@/components/Option';
import Stack from '@/components/Stack';
import TextInput from '@/components/TextInput';
import TooltipInfo from '@/components/TooltipInfo';
import { Heading, Subheading, Small } from '@/components/Typography';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import {
  SEPARATE_INVOICE_BY_MONTH,
  CONSOLIDATE_CHARGES,
  SWEEP_HINT,
  SEPARATE_INVOICE_HINT,
} from '@/constants/text';
import Text from '@/elements/Text';
import FormColumns from '@/form/FormColumns';
import useToggle from '@/hooks/useToggle';
import { Customer } from '@/routes/Agency/CreateOrder/context';
import { AccountFormValues } from '@/routes/Agency/common/components/AccountForm';
import POBoxFormFields from '@/routes/Agency/common/components/AccountForm/POBoxFormFields';
import { getSchemaByContext } from '@/routes/Agency/common/components/AccountForm/schema';
import { Role } from '@/routes/PrivateRoute';
import {
  BillingWeekEnum,
  InvoiceCutOffEnum,
  InvoiceGroupByEnum,
} from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';

interface Props {
  customer: Customer;
  selectedAccount?: Record<string, any>;
  handleCancel: () => void;
  handleSuccess: () => void;
  phoneOnly: boolean;
}

const AddBillingAccount = ({
  customer,
  selectedAccount,
  handleCancel,
  handleSuccess,
  phoneOnly,
}: Props) => {
  const defaultValues: DefaultValues<AccountFormValues> = {
    accountName: selectedAccount?.name,
    defaultContact: selectedAccount?.defaultContact,
    address: {
      addressLine1: selectedAccount?.addressLine1 ?? undefined,
      addressLine2: selectedAccount?.addressLine2 ?? '',
      city: selectedAccount?.city ?? undefined,
      state: selectedAccount?.state ?? undefined,
      zip: selectedAccount?.zip ?? undefined,
    },
    invoiceDuration: selectedAccount?.cutOff ?? InvoiceCutOffEnum.WEEKLY,
    billingWeek: selectedAccount?.billingWeek ?? BillingWeekEnum.MONDAY,
    crossMonthInvoice: false,
    invoiceSweepRule: selectedAccount?.consolidateUnprocessedCharges || true,
    groupBy: selectedAccount?.groupBy || InvoiceGroupByEnum.NONE,
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    control,
    setValue,
  } = useForm<AccountFormValues>({
    resolver: yupResolver(getSchemaByContext(selectedAccount ? 'EDIT' : 'ADD')),
    mode: 'onChange',
    defaultValues,
  });
  const [isPOBox, togglePOBox] = useToggle();
  const customWorkWeek = useFeatureValue(FEATURE_TOGGLE.CustomWorkWeek, false);
  const invoiceSweepRuleFeature = useFeatureValue(
    FEATURE_TOGGLE.InvoiceSweepRule,
    false
  );
  const invoiceGroupRule = useFeatureValue(
    FEATURE_TOGGLE.InvoiceGroupRule,
    false
  );

  const currentAdmin = useReactiveVar(currentAdminVar);
  const isTenantAdmin = currentAdmin?.role === Role.TENANT_ADMIN;
  const isDurationWeekly =
    control._formValues.invoiceDuration === InvoiceCutOffEnum.WEEKLY;

  const handleOnClickPOBoxOption = () => {
    togglePOBox();
    setValue('address.addressLine2', undefined);
  };
  const [showAddPayment, setShowAddPayment] = useState(false);

  const onSubmit: SubmitHandler<AccountFormValues> = () => {
    setShowAddPayment(true);
  };

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNext fontSize="small" />}
      >
        {(!phoneOnly || (phoneOnly && !showAddPayment)) && (
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            underline="hover"
            onClick={handleCancel}
          >
            <Text color="lighter">Select billing account</Text>
          </Link>
        )}

        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          underline="hover"
          onClick={() => {
            setShowAddPayment(false);
          }}
        >
          <Text>Add Billing Account</Text>
        </Link>
        {showAddPayment && (
          <Link color="inherit" sx={{ cursor: 'pointer' }} underline="hover">
            <Text>Add payment method</Text>
          </Link>
        )}
      </Breadcrumbs>
      {showAddPayment && (
        <AddPaymentMethod
          custId={customer.id}
          getBillingValues={getValues}
          handleCancel={handleCancel}
          handleSuccess={handleSuccess}
          phoneOnly={phoneOnly}
        />
      )}
      {!showAddPayment && (
        <>
          <Heading>Add billing account</Heading>
          <form
            autoComplete="off"
            style={{ width: '100%' }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <ConditionalWrapper
              condition={!phoneOnly}
              wrapper={(children) => (
                <Card
                  sx={{ border: `1px solid #D3D3D3`, width: '100%' }}
                  variant="outlined"
                >
                  <CardContent sx={{ paddingBottom: '14px !important' }}>
                    <Subheading css={{ marginBottom: '8px' }}>
                      Account info
                    </Subheading>
                    {children}
                  </CardContent>
                </Card>
              )}
            >
              <FormColumns css={{ marginTop: '6px' }}>
                <FormElement htmlFor="accountName" label="Account Name">
                  <TextInput
                    autoFocus
                    id="accountName"
                    placeholder="Account Name"
                    {...register('accountName')}
                  />
                  {errors.accountName && (
                    <Small color="danger" role="alert">
                      {errors.accountName.message}
                    </Small>
                  )}
                </FormElement>
                <FormElement htmlFor="defaultContact" label="Default Contact">
                  <Controller
                    control={control}
                    name="defaultContact"
                    render={({ field: { onChange } }) => (
                      <ItemSelect<any>
                        disabled={customer.admins.items.length === 0}
                        id="defaultContact"
                        itemToKey={(item) => item.id}
                        itemToString={(item) =>
                          typeof item !== 'string'
                            ? `${item?.user.firstName} ${item?.user.lastName}`
                            : ''
                        }
                        items={customer.admins.items}
                        placeholder={
                          customer.admins.items.length === 0
                            ? 'No contacts available, please create a new Administrator'
                            : 'Select default contact...'
                        }
                        selectedItem={getValues('defaultContact')}
                        onSelectedItemChange={({ selectedItem }) =>
                          onChange(selectedItem)
                        }
                      />
                    )}
                  />
                  {errors.defaultContact && (
                    <Small color="danger" role="alert">
                      {errors.defaultContact?.message}
                    </Small>
                  )}
                </FormElement>
              </FormColumns>

              <Divider sx={{ my: '8px' }} />
              <Subheading css={{ marginTop: '16px', marginBottom: '10px' }}>
                Address details
              </Subheading>

              <FormElement>
                <Option
                  appearance="checkbox"
                  id="isPOBox"
                  label="Is PO Box?"
                  onClick={handleOnClickPOBoxOption}
                />
              </FormElement>
              <ConditionalWrapper
                condition={!isPOBox}
                wrapper={(children) => <FormColumns>{children}</FormColumns>}
              >
                {isPOBox ? (
                  <POBoxFormFields
                    {...{ register, errors, control, setValue }}
                  />
                ) : (
                  <>
                    <FormElement htmlFor="address" label="Address Line 1">
                      <Controller
                        control={control}
                        name="address"
                        render={({ field: { value, onChange } }) => (
                          <AddressAutocomplete
                            setValues={onChange}
                            values={value}
                          >
                            {errors.address && (
                              <Small color="danger" role="alert">
                                Address is a required field
                              </Small>
                            )}
                          </AddressAutocomplete>
                        )}
                      />
                    </FormElement>
                    <FormElement htmlFor="addressLine2" label="Address Line 2">
                      <Controller
                        control={control}
                        name="address.addressLine2"
                        render={({ field }) => (
                          <TextInput
                            id="addressLine2"
                            placeholder="Type address..."
                            {...field}
                          />
                        )}
                      />
                    </FormElement>
                  </>
                )}
              </ConditionalWrapper>
              <Divider sx={{ mb: '8px' }} />
              <Subheading css={{ marginTop: '18px', marginBottom: '12px' }}>
                Preference
              </Subheading>

              <Controller
                control={control}
                name="invoiceDuration"
                render={({ field: { value, onChange } }) => {
                  return (
                    <InvoiceDurationFormField
                      invoiceDuration={value}
                      showJobOption={invoiceGroupRule}
                      onChangeInvoiceDuration={(val) => {
                        onChange(val);
                        if (val !== InvoiceCutOffEnum.WEEKLY) {
                          setValue('invoiceSweepRule', true);
                          setValue('groupBy', InvoiceGroupByEnum.NONE);
                          setValue('billingWeek', BillingWeekEnum.MONDAY);
                        }
                      }}
                    />
                  );
                }}
              />
              {isTenantAdmin && isDurationWeekly && (
                <>
                  {customWorkWeek && (
                    <Controller
                      control={control}
                      name="billingWeek"
                      render={({ field: { value, onChange } }) => {
                        return (
                          <BillingWeekFormField
                            billingWeek={value}
                            onChangeBillingWeek={onChange}
                          />
                        );
                      }}
                    />
                  )}
                  {invoiceGroupRule && (
                    <Controller
                      control={control}
                      name="groupBy"
                      render={({ field: { value, onChange } }) => {
                        return (
                          <GroupInvoicesFormField
                            groupBy={value}
                            onChangeGroupBy={onChange}
                          />
                        );
                      }}
                    />
                  )}
                </>
              )}

              <Stack vertical>
                {invoiceSweepRuleFeature && isTenantAdmin && isDurationWeekly && (
                  <Controller
                    control={control}
                    name="invoiceSweepRule"
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Option
                          appearance="checkbox"
                          checked={value}
                          id="invoice-sweep-rule"
                          label={
                            <>
                              <span style={{ marginRight: '10px' }}>
                                {CONSOLIDATE_CHARGES}
                              </span>
                              <TooltipInfo text={SWEEP_HINT} />
                            </>
                          }
                          onChange={onChange}
                        />
                      );
                    }}
                  />
                )}
                <Controller
                  control={control}
                  name="crossMonthInvoice"
                  render={({ field: { value, onChange } }) => {
                    return (
                      <Option
                        appearance="checkbox"
                        checked={value}
                        id="invoice-cross-month"
                        label={
                          <>
                            <span style={{ marginRight: '10px' }}>
                              {SEPARATE_INVOICE_BY_MONTH}
                            </span>
                            <TooltipInfo text={SEPARATE_INVOICE_HINT} />
                          </>
                        }
                        onChange={onChange}
                      />
                    );
                  }}
                />
              </Stack>
            </ConditionalWrapper>

            <Stack css={{ paddingTop: '16px' }}>
              <Button
                a11yLabel="Submit form"
                disabled={!isValid}
                id="continue-to-payment-btn"
                label="Continue to payment method"
                type="submit"
              />
              <Button
                a11yLabel="Go back to previous step"
                appearance="outline"
                id="cancel-add-acc-btn"
                label="Cancel"
                type="button"
                onClick={handleCancel}
              />
            </Stack>
          </form>
        </>
      )}
    </>
  );
};

export default AddBillingAccount;
